import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import * as firebaseCourses from '../../services/firebaseCourses';
import { getMyMessagesWithReplies } from '../../services/courseMessages';
import './Navbar.css';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const INFOBLOOM_WHATSAPP_URL = 'https://wa.me/2349014088590?text=Hi%2C%20I%27m%20interested%20in%20becoming%20a%20course%20creator%20on%20Infobloom.';

export default function Navbar() {
  const { user, logout, isAuthenticated } = useAuth();
  const { count: cartCount } = useCart();
  const { language, setLanguage, languages, t } = useLanguage();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const languageRef = useRef(null);
  const searchRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (languageRef.current && !languageRef.current.contains(e.target)) setLanguageOpen(false);
    }
    if (languageOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [languageOpen]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    }
    if (searchOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [searchOpen]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    }
    if (notifOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [notifOpen]);

  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      setNotifications([]);
      return;
    }
    setNotifLoading(true);
    getMyMessagesWithReplies(user.uid)
      .then(setNotifications)
      .catch(() => setNotifications([]))
      .finally(() => setNotifLoading(false));
  }, [isAuthenticated, user?.uid]);

  useEffect(() => {
    if (menuOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const q = search.trim();
    if (q.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    const t = setTimeout(() => {
      setSearchLoading(true);
      setSearchOpen(true);
      firebaseCourses.listCoursesForHome()
        .then((list) => {
          const all = list || [];
          const qq = q.toLowerCase();
          const filtered = all.filter((course) =>
            (course.title || '').toLowerCase().includes(qq) ||
            (course.instructor || '').toLowerCase().includes(qq)
          );
          setSearchResults(filtered.slice(0, 8));
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/?q=${encodeURIComponent(search.trim())}`);
    setSearchOpen(false);
  };

  const goToCourse = (courseId) => {
    setSearch('');
    setSearchResults([]);
    setSearchOpen(false);
    closeMenu();
    navigate(`/course/${courseId}`);
  };

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    setMenuOpen(false);
    navigate('/');
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setSearchOpen(false);
  };

  return (
      <header className="navbar">
        <div className="navbar-inner">
          <Link to="/" className="navbar-logo" onClick={closeMenu}>
            <span className="navbar-logo-img-wrap">
              <img src={`${process.env.PUBLIC_URL || ''}/logo.jpg`} alt="Infobloom" className="navbar-logo-img" />
            </span>
            <span className="navbar-logo-text">{t('navbar.brand')}</span>
          </Link>
          <div className="navbar-search-wrap" ref={searchRef}>
            <form className="navbar-search navbar-search-desk" onSubmit={handleSearch}>
              <button type="submit" className="navbar-search-btn" aria-label="Search">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </button>
              <input
                type="search"
                placeholder={t('navbar.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                className="navbar-search-input"
                autoComplete="off"
              />
            </form>
            {searchOpen && (search.trim().length >= MIN_SEARCH_LENGTH) && (
              <div id="navbar-search-results" className="navbar-search-dropdown" role="listbox">
                {searchLoading ? (
                  <p className="navbar-search-dropdown-loading">{t('navbar.search.loading')}</p>
                ) : searchResults.length === 0 ? (
                  <p className="navbar-search-dropdown-empty">{t('navbar.search.empty')}</p>
                ) : (
                  <ul className="navbar-search-dropdown-list">
                    {searchResults.map((course) => (
                      <li key={course.id}>
                        <button
                          type="button"
                          className="navbar-search-dropdown-item"
                          onClick={() => goToCourse(course.id)}
                        >
                          {course.thumbnail ? (
                            <img src={course.thumbnail} alt="" className="navbar-search-dropdown-img" />
                          ) : (
                            <span className="navbar-search-dropdown-img-placeholder" />
                          )}
                          <span className="navbar-search-dropdown-info">
                            <span className="navbar-search-dropdown-title">{course.title}</span>
                            <span className="navbar-search-dropdown-meta">{(course.instructor || 'Instructor')}{course.level ? ` · ${course.level}` : ''}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="navbar-menu-btn"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => { setMenuOpen(!menuOpen); setProfileOpen(false); setLanguageOpen(false); }}
          >
            <span className="navbar-menu-icon" />
            <span className="navbar-menu-icon" />
            <span className="navbar-menu-icon" />
          </button>
          <nav className="navbar-actions">
            {isAuthenticated && <Link to="/my-learning" className="navbar-link">{t('navbar.myLearning')}</Link>}
            <Link to="/playground" className="navbar-link">Code Editor</Link>
            <a href={INFOBLOOM_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="navbar-link navbar-link-teach" title={t('navbar.whatsapp')}>
              {t('navbar.teach')}
            </a>
            <a href={INFOBLOOM_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="navbar-icon-btn navbar-whatsapp-btn" aria-label={t('navbar.whatsapp')} title={t('navbar.whatsapp')}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </a>

            {isAuthenticated && (
              <div className="navbar-dropdown-wrap navbar-notif-wrap" ref={notifRef}>
                <button
                  type="button"
                  className="navbar-icon-btn navbar-notif-btn"
                  aria-label={notifications.length > 0 ? `${notifications.length} reply from course owner` : 'Notifications'}
                  aria-expanded={notifOpen}
                  onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); setLanguageOpen(false); }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {notifications.length > 0 && <span className="navbar-notif-count">{notifications.length}</span>}
                </button>
                {notifOpen && (
                  <div className="navbar-dropdown navbar-notif-dropdown navbar-dropdown-right">
                    <p className="navbar-notif-title">Replies from course owner</p>
                    {notifLoading ? (
                      <p className="navbar-notif-empty">Loading…</p>
                    ) : notifications.length === 0 ? (
                      <p className="navbar-notif-empty">No new replies.</p>
                    ) : (
                      <ul className="navbar-notif-list">
                        {notifications.map((n) => (
                          <li key={n.id} className="navbar-notif-item">
                            <Link
                              to={`/course/${n.courseId}`}
                              className="navbar-notif-item-link"
                              onClick={() => { setNotifOpen(false); closeMenu(); }}
                            >
                              <span className="navbar-notif-item-course">{n.courseTitle || 'Course'}</span>
                              <span className="navbar-notif-item-reply">{String(n.adminReply).slice(0, 80)}{String(n.adminReply).length > 80 ? '…' : ''}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="navbar-notif-footer">
                      <Link to="/messages" className="navbar-notif-open-messages" onClick={() => { setNotifOpen(false); closeMenu(); }}>
                        Open Messages
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Link to="/cart" className="navbar-icon-btn navbar-cart-btn" aria-label={`Cart${cartCount > 0 ? `, ${cartCount} items` : ''}`}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              {cartCount > 0 && <span className="navbar-cart-count">{cartCount}</span>}
            </Link>
            {!isAuthenticated && (
              <>
                <Link to="/login" className="navbar-btn navbar-btn-outline">{t('navbar.login')}</Link>
                <Link to="/signup" className="navbar-btn navbar-btn-primary">{t('navbar.signup')}</Link>
              </>
            )}
            <button
              type="button"
              className="navbar-icon-btn navbar-theme-btn"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={toggleTheme}
            >
              {isDark ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" /></svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
              )}
            </button>
            <div className="navbar-dropdown-wrap navbar-language-wrap" ref={languageRef}>
              <button
                type="button"
                className="navbar-icon-btn"
                aria-label={t('navbar.languageTitle')}
                aria-expanded={languageOpen}
                onClick={() => { setLanguageOpen(!languageOpen); setProfileOpen(false); }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </button>
              {languageOpen && (
                <div className="navbar-dropdown navbar-language-dropdown navbar-dropdown-right">
                  <p className="navbar-language-title">{t('navbar.languageTitle')}</p>
                  <div className="navbar-language-list">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        className="navbar-language-item"
                        onClick={() => { setLanguage(lang); setLanguageOpen(false); }}
                      >
                        <span className="navbar-language-name">{lang.name}</span>
                        {language.code === lang.code && (
                          <svg className="navbar-language-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {isAuthenticated && (
              <div className="navbar-dropdown-wrap">
                <button type="button" className="navbar-profile-btn navbar-profile-btn-main" onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }}>
                  <span className="navbar-avatar navbar-avatar-small">{user?.name?.charAt(0) || 'S'}</span>
                </button>
                {profileOpen && (
                  <div className="navbar-dropdown navbar-dropdown-profile navbar-dropdown-right">
                    <p className="navbar-dropdown-name">{user?.name}</p>
                    <p className="navbar-dropdown-email">{user?.email}</p>
                    <Link to="/profile" className="navbar-dropdown-item" onClick={() => setProfileOpen(false)}>Profile & Settings</Link>
                    <Link to="/my-learning" className="navbar-dropdown-item" onClick={() => setProfileOpen(false)}>My Learning</Link>
                    <button type="button" className="navbar-dropdown-item navbar-dropdown-logout" onClick={handleLogout}>Log out</button>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
        <div
            className={`navbar-mobile-menu ${menuOpen ? 'navbar-mobile-menu-open' : ''}`}
            aria-hidden={!menuOpen}
            onClick={(e) => { if (e.target === e.currentTarget) closeMenu(); }}
          >
          <div className="navbar-mobile-menu-inner" onClick={(e) => e.stopPropagation()}>
            <div className="navbar-search-wrap navbar-search-wrap-mobile" ref={searchRef}>
              <form className="navbar-search navbar-search-mobile" onSubmit={(e) => { handleSearch(e); closeMenu(); }}>
                <button type="submit" className="navbar-search-btn" aria-label="Search">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </button>
                <input
                  type="search"
                  placeholder="Search courses..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                  className="navbar-search-input"
                  autoComplete="off"
                />
              </form>
              {searchOpen && (search.trim().length >= MIN_SEARCH_LENGTH) && (
                <div className="navbar-search-dropdown navbar-search-dropdown-mobile">
                  {searchLoading ? (
                    <p className="navbar-search-dropdown-loading">Searching...</p>
                  ) : searchResults.length === 0 ? (
                    <p className="navbar-search-dropdown-empty">No courses match your search.</p>
                  ) : (
                    <ul className="navbar-search-dropdown-list">
                      {searchResults.map((course) => (
                        <li key={course.id}>
                          <button
                            type="button"
                            className="navbar-search-dropdown-item"
                            onClick={() => goToCourse(course.id)}
                          >
                            {course.thumbnail ? (
                              <img src={course.thumbnail} alt="" className="navbar-search-dropdown-img" />
                            ) : (
                              <span className="navbar-search-dropdown-img-placeholder" />
                            )}
                            <span className="navbar-search-dropdown-info">
                              <span className="navbar-search-dropdown-title">{course.title}</span>
                              <span className="navbar-search-dropdown-meta">{(course.instructor || 'Instructor')}{course.level ? ` · ${course.level}` : ''}</span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="navbar-mobile-links">
              <a href={INFOBLOOM_WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="navbar-mobile-link navbar-mobile-link-teach" onClick={closeMenu}>{t('navbar.teach')}</a>
              <Link to="/cart" className="navbar-mobile-link" onClick={closeMenu}>Cart{cartCount > 0 ? ` (${cartCount})` : ''}</Link>
              <Link to="/playground" className="navbar-mobile-link" onClick={closeMenu}>Code Editor</Link>
              {isAuthenticated && (
                <>
                  <Link to="/my-learning" className="navbar-mobile-link" onClick={closeMenu}>My Learning</Link>
                  <Link to="/messages" className="navbar-mobile-link" onClick={closeMenu}>Messages{notifications.length > 0 ? ` (${notifications.length})` : ''}</Link>
                </>
              )}
              {!isAuthenticated && (
                <>
                  <Link to="/login" className="navbar-mobile-link" onClick={closeMenu}>Log in</Link>
                  <Link to="/signup" className="navbar-mobile-link navbar-mobile-link-primary" onClick={closeMenu}>Sign up</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
  );
}
